import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Send, Paperclip, X, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
const ALLOWED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

const MessagingSystem = ({ 
  operationId, 
  messages: initialMessages = [], 
  assignedOperator = null,
  assignedFieldOps = null
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load selected recipient from localStorage on mount
  useEffect(() => {
    const storedRecipient = localStorage.getItem('selected_message_recipient');
    if (storedRecipient) {
      try {
        const recipient = JSON.parse(storedRecipient);
        setSelectedRecipient(recipient);
        // Clear after loading
        localStorage.removeItem('selected_message_recipient');
      } catch (e) {
        console.error('Error parsing stored recipient:', e);
      }
    } else if (assignedOperator) {
      // Default to operator if no selection
      setSelectedRecipient({ type: 'operator', name: assignedOperator.name, id: assignedOperator.id });
    } else if (assignedFieldOps) {
      // Fallback to field ops
      setSelectedRecipient({ type: 'field_ops', name: assignedFieldOps.name, id: assignedFieldOps.id });
    }
  }, [assignedOperator, assignedFieldOps]);

  // Fetch messages on mount and setup polling
  useEffect(() => {
    if (operationId) {
      fetchMessages();
      
      // Poll for new messages every 10 seconds
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [operationId]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages?operation_id=${operationId}`);
      setMessages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    // Validate files
    const validFiles = [];
    const errors = [];
    
    files.forEach(file => {
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      
      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(fileExt)) {
        errors.push(`${file.name}: format non autorisé`);
        return;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: trop volumineux (max 15 Mo)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    if (errors.length > 0) {
      toast({
        title: "Erreur de validation",
        description: errors.join(', '),
        variant: "destructive"
      });
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return [];
    
    setUploading(true);
    const uploadedAttachments = [];
    
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/messages/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedAttachments.push(response.data);
      }
      
      return uploadedAttachments;
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: "Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader les fichiers",
        variant: "destructive"
      });
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    setSending(true);

    try {
      // Upload files first if any
      let attachments = [];
      if (selectedFiles.length > 0) {
        attachments = await uploadFiles();
      }
      
      const response = await axios.post(`${API}/messages`, {
        operationId,
        content: newMessage || "📎 Pièce(s) jointe(s)",
        attachments: attachments,
        recipientType: selectedRecipient?.type || 'operator',
        recipientId: selectedRecipient?.id
      });
      
      // Clear inputs
      setNewMessage('');
      setSelectedFiles([]);
      
      // Refresh messages immediately
      await fetchMessages();
      
      toast({
        title: "✅ Message envoyé",
        description: selectedRecipient 
          ? `Votre message a été envoyé à ${selectedRecipient.name}`
          : "Votre message a été envoyé à l'équipe Baobab"
      });
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer le message",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if we have multiple contacts
  const hasMultipleContacts = assignedOperator && assignedFieldOps;

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="border-b">
        <CardTitle className="text-lg">Messagerie</CardTitle>
        
        {/* Recipient Selector */}
        {hasMultipleContacts ? (
          <div className="space-y-2 mt-2">
            <p className="text-xs text-gray-500">Destinataire :</p>
            <div className="flex gap-2">
              {/* Operator Option */}
              <button
                onClick={() => setSelectedRecipient({ type: 'operator', name: assignedOperator.name, id: assignedOperator.id })}
                className={`flex-1 flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                  selectedRecipient?.type === 'operator'
                    ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-purple-600 text-white text-xs">
                    {getInitials(assignedOperator.name || 'OP')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-xs font-semibold text-gray-900">
                    {assignedOperator.name}
                  </p>
                  <p className="text-xs text-purple-600">Opérateur</p>
                </div>
              </button>
              
              {/* Field Ops Option */}
              <button
                onClick={() => setSelectedRecipient({ type: 'field_ops', name: assignedFieldOps.name, id: assignedFieldOps.id })}
                className={`flex-1 flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                  selectedRecipient?.type === 'field_ops'
                    ? 'bg-cyan-50 border-cyan-300 ring-2 ring-cyan-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-cyan-600 text-white text-xs">
                    {getInitials(assignedFieldOps.name || 'FO')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-xs font-semibold text-gray-900">
                    {assignedFieldOps.name}
                  </p>
                  <p className="text-xs text-cyan-600">Field Ops</p>
                </div>
              </button>
            </div>
          </div>
        ) : assignedOperator ? (
          <div className="flex items-center gap-2 mt-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-purple-600 text-white text-xs">
                {getInitials(assignedOperator.name || 'OP')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-sm font-semibold text-purple-900">
                {assignedOperator.name}
              </p>
              <p className="text-xs text-purple-600">
                👤 Votre opérateur assigné
              </p>
            </div>
          </div>
        ) : assignedFieldOps ? (
          <div className="flex items-center gap-2 mt-2 p-2 bg-cyan-50 rounded-lg border border-cyan-200">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-cyan-600 text-white text-xs">
                {getInitials(assignedFieldOps.name || 'FO')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-sm font-semibold text-cyan-900">
                {assignedFieldOps.name}
              </p>
              <p className="text-xs text-cyan-600">
                👤 Votre Field Ops assigné
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Communiquez avec l'équipe Baobab</p>
        )}
      </CardHeader>
      
      <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Chargement des messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucun message pour le moment</p>
              <p className="text-sm text-gray-400 mt-2">Commencez la conversation ci-dessous</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.senderId === user?.id;
              const isAdmin = ['admin', 'commercial'].includes(message.senderType);
              const isOperator = message.senderType === 'operator';

              return (
                <div
                  key={message.id}
                  className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback 
                      className={
                        isAdmin || isOperator 
                          ? 'bg-[#1db584] text-white' 
                          : 'bg-gray-200 text-gray-700'
                      }
                    >
                      {getInitials(message.senderName)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className={`flex-1 max-w-[70%] ${isOwnMessage ? 'items-end' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-700">
                        {message.senderName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(message.createdAt)}
                      </span>
                    </div>
                    
                    <div
                      className={`p-3 rounded-lg ${
                        isOwnMessage
                          ? 'bg-[#1db584] text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.content}
                      </p>
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {message.attachments.map((attachment, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-2 p-2 rounded ${
                                isOwnMessage ? 'bg-white/20' : 'bg-white'
                              }`}
                            >
                              {attachment.fileType === 'image' ? (
                                <a 
                                  href={`${BACKEND_URL}${attachment.url}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={`${BACKEND_URL}${attachment.url}`} 
                                    alt={attachment.filename}
                                    className="max-w-[200px] max-h-[200px] rounded cursor-pointer hover:opacity-80"
                                  />
                                </a>
                              ) : (
                                <>
                                  {attachment.fileType === 'pdf' ? (
                                    <FileText className="w-5 h-5 text-red-500" />
                                  ) : (
                                    <FileText className="w-5 h-5 text-blue-500" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                                      {attachment.filename}
                                    </p>
                                    <p className={`text-xs ${isOwnMessage ? 'text-white/70' : 'text-gray-500'}`}>
                                      {(attachment.fileSize / 1024).toFixed(1)} KB
                                    </p>
                                  </div>
                                  <a
                                    href={`${BACKEND_URL}${attachment.url}`}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className={`h-8 w-8 p-0 ${isOwnMessage ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </a>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t p-4">
          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg"
                >
                  {file.type.startsWith('image/') ? (
                    <ImageIcon className="w-4 h-4 text-blue-500" />
                  ) : (
                    <FileText className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="text-xs font-medium text-gray-700 max-w-[150px] truncate">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveFile(index)}
                    className="h-5 w-5 p-0 hover:bg-gray-200"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploading}
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Tapez votre message..."
              className="flex-1"
              disabled={sending || uploading}
            />
            
            <Button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending || uploading}
              className="bg-[#1db584] hover:bg-[#16a075] text-white shrink-0"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Upload...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Appuyez sur Entrée pour envoyer • Formats autorisés : PDF, JPG, PNG, DOC, DOCX (max 15 Mo)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MessagingSystem;
